import { TranslateService } from '@ngx-translate/core';

import { PlanningSpaceMetadataModel } from '@core/models/planning-space/planning-space.model';
import { OverallDeployStatus } from '@core/utils/deploy-status.utils';
import { isNil } from '@core/utils/shared-utils';

import {
  AvailableFilterField,
  FilterDataType
} from '@shared/components/filter-query-builder/filter.models';
import { SortableFieldRef } from '@shared/utils/filter-preset.utils';

interface PlanningSpaceFilterColumn {
  columnKey: keyof PlanningSpaceMetadataModel;
  dbColName: string;
  labelKey: string;
  dataType: FilterDataType;
  /** Only a real `PlanningSpace` column can be ordered on server-side. */
  sortable: boolean;
  /** Typed on the status rollup, the only SELECT column this list exposes. */
  availableValues?: OverallDeployStatus[];
}

/**
 * The single description of what this list can be filtered and sorted on; everything else in
 * this file is derived from it, so a column can never be filterable without a matching field.
 *
 * `deploy_status` is not a `PlanningSpace` column: the backend rolls up the latest
 * `DeployPlanStatus` per channel of the most recently deployed plan, which is what the status
 * column renders. Matching any historical row would let a space that failed then succeeded
 * satisfy an ERROR filter while displaying green.
 */
const PLANNING_SPACE_FILTER_COLUMNS: PlanningSpaceFilterColumn[] = [
  {
    columnKey: 'name',
    dbColName: 'name',
    labelKey: 'common.name',
    dataType: FilterDataType.TEXT,
    sortable: true
  },
  {
    columnKey: 'nbPlans',
    dbColName: 'nb_plans',
    labelKey: 'features.planning_space_list.table.plans',
    dataType: FilterDataType.NUMBER,
    sortable: true
  },
  {
    columnKey: 'createdAt',
    dbColName: 'created_at',
    labelKey: 'common.created',
    dataType: FilterDataType.DATE,
    sortable: true
  },
  {
    columnKey: 'updatedAt',
    dbColName: 'updated_at',
    labelKey: 'common.modified',
    dataType: FilterDataType.DATE,
    sortable: true
  },
  {
    columnKey: 'lastDeployStatus',
    dbColName: 'deploy_status',
    labelKey: 'features.plan.deploy.status_detail',
    dataType: FilterDataType.SELECT,
    sortable: false,
    // `NONE` is the "—" rendered for a never-deployed space: the absence of a deploy row
    // rather than a stored status. Without it those spaces match no status filter at all.
    availableValues: ['ERROR', 'PROCESSING', 'SUCCESS', 'NONE']
  }
];

type ColumnToDbColName = Partial<Record<keyof PlanningSpaceMetadataModel, string>>;

function columnMap(columns: PlanningSpaceFilterColumn[]): ColumnToDbColName {
  return Object.fromEntries(columns.map((col) => [col.columnKey, col.dbColName]));
}

const sortableColumns = PLANNING_SPACE_FILTER_COLUMNS.filter((col) => col.sortable);

export const PLANNING_SPACE_FILTERABLE_COLUMNS = columnMap(PLANNING_SPACE_FILTER_COLUMNS);
export const PLANNING_SPACE_SORTABLE_COLUMNS = columnMap(sortableColumns);

export function makePlanningSpaceFilterFields(
  translate: TranslateService
): AvailableFilterField[] {
  return PLANNING_SPACE_FILTER_COLUMNS.map((col) => ({
    field: col.dbColName,
    dbColumnName: col.dbColName,
    label: translate.instant(col.labelKey),
    dataType: col.dataType,
    ...(isNil(col.availableValues) ? {} : { availableValues: col.availableValues })
  }));
}

export function makePlanningSpaceSortableFields(
  translate: TranslateService
): SortableFieldRef[] {
  return sortableColumns.map((col) => ({
    columnKey: col.columnKey,
    dbColName: col.dbColName,
    label: translate.instant(col.labelKey)
  }));
}
